'use client';

import Box from '@mui/material/Box';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { composeSx } from '../../utils/styledUtils';

export interface ReasoningCalloutProps {
  /** Body content — the reasoning prose. Accepts ReactNode so consumers can embed CitationChips. */
  children: ReactNode;
  /** Optional attribution label (e.g. "Astor's reasoning", "Underwriter override"). */
  attribution?: ReactNode;
  /** Optional leading icon for the attribution row. */
  icon?: ReactNode;
  /** Optional footer slot — typically a row of CitationChips / ToolCallChips. */
  footer?: ReactNode;
  /**
   * Visual tone. 'success' (default) for green-tinted Astor reasoning; 'warning' for caution
   * callouts; 'neutral' for plain quotation.
   */
  tone?: 'success' | 'warning' | 'neutral' | 'info';
  /** Sx overrides on the outer Box. */
  sx?: SxProps<Theme>;
}

function toneColors(theme: Theme, tone: ReasoningCalloutProps['tone']) {
  const isDark = theme.palette.mode === 'dark';
  if (tone === 'neutral') {
    return {
      // Reads as a distinct callout (not body text); higher opacity than tonal alpha to compensate for no chroma.
      bg: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
      border: isDark ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.14)',
      accent: theme.palette.text.secondary,
    };
  }
  const palette = theme.palette[tone ?? 'success'];
  return {
    bg: alpha(palette.main, isDark ? 0.08 : 0.06),
    border: alpha(palette.main, isDark ? 0.28 : 0.22),
    accent: palette.main,
  };
}

export function ReasoningCallout({ children, attribution, icon, footer, tone = 'success', sx }: ReasoningCalloutProps) {
  const theme = useTheme();
  const colors = toneColors(theme, tone);

  return (
    <Box
      role="note"
      sx={composeSx(
        {
          position: 'relative',
          my: 1.5,
          pl: 1.75,
          pr: 1.5,
          py: 1.25,
          borderRadius: 1,
          backgroundColor: colors.bg,
          border: `1px solid ${colors.border}`,
          // Left accent bar
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 8,
            bottom: 8,
            width: 2,
            borderRadius: 2,
            backgroundColor: colors.accent,
          },
        },
        sx,
      )}
    >
      {(attribution || icon) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
          {icon && (
            <Box
              aria-hidden
              sx={{
                display: 'inline-flex',
                color: colors.accent,
                fontSize: '0.875rem',
                lineHeight: 1,
              }}
            >
              {icon}
            </Box>
          )}
          {attribution && (
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: colors.accent,
                textTransform: 'uppercase',
                letterSpacing: '0.08em',
              }}
            >
              {attribution}
            </Typography>
          )}
        </Box>
      )}
      <Typography variant="body2" component="div" sx={{ color: 'text.primary', lineHeight: 1.55 }}>
        {children}
      </Typography>
      {footer && (
        <Box
          sx={{
            mt: 1,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 0.75,
            alignItems: 'center',
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
}

ReasoningCallout.displayName = 'ReasoningCallout';

export default ReasoningCallout;
